import { useState, useEffect } from "react";
import api from "../services/api";
import { getCourses, createModule as createModuleApi, createLesson, updateModule, deleteModule, updateLesson, deleteLesson } from "../services/courses";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import Sidebar from "../components/Sidebar";
import {
  FolderPlus,
  Upload,
  PlayCircle,
  ImageIcon,
  FileText,
  Plus,
  Edit3,
  Trash2,
  X,
  ClipboardCheck,
  Video
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';

export default function TeacherClasses({ defaultCourseId, isEmbedded }) {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [modules, setModules] = useState([]);
  const [moduleTitle, setModuleTitle] = useState("");
  const [courseImages, setCourseImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingModule, setEditingModule] = useState(null);
  const [editModuleTitle, setEditModuleTitle] = useState("");
  const [editingLesson, setEditingLesson] = useState(null);
  const [editLessonTitle, setEditLessonTitle] = useState("");

  useEffect(() => {
    const init = async () => {
      const token = getToken();
      if (!token) return;
      const user = jwtDecode(token);

      try {
        const res = await getCourses();
        const myCourses = res.data;
        setCourses(myCourses);
        if (myCourses.length > 0) {
          const firstCourseId = defaultCourseId ? Number(defaultCourseId) : myCourses[0].id;
          setSelectedCourse(firstCourseId);
          loadModules(firstCourseId);
          loadCourseImages(firstCourseId);
        }
      } catch (err) {
        toast.error("Failed to load courses");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const loadModules = async (courseId) => {
    if (!courseId) return;
    try {
      const res = await api.get(`/courses/${courseId}/curriculum`);
      setModules(res.data);
    } catch (err) {
      console.error("Failed to load curriculum");
    }
  };

  const loadCourseImages = async (courseId) => {
    if (!courseId) return;
    try {
      const res = await api.get(`/courses/${courseId}/images`);
      setCourseImages(res.data);
    } catch (err) {
      console.error("Failed to load images");
    }
  };

  const handleCreateModule = async () => {
    if (!selectedCourse || !moduleTitle) return toast.error("Module info missing");
    try {
      await createModuleApi(selectedCourse, moduleTitle);
      setModuleTitle("");
      loadModules(selectedCourse);
      toast.success("Module added!");
    } catch (err) {
      toast.error("Failed to add module");
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    const loadingToast = toast.loading("Uploading image...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/file", formData);
      const file_url = res.data.url;
      await api.post(`/courses/${selectedCourse}/images`, {
        image_url: file_url,
        is_preview: courseImages.length === 0 ? 1 : 0,
        order: courseImages.length
      });

      loadCourseImages(selectedCourse);
      toast.dismiss(loadingToast);
      toast.success("Image added to gallery!");
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Upload failed");
    }
  };

  const handleUpload = async (file, moduleId, lessonType = "video") => {
    if (!file) return;
    const isPdf = lessonType === "pdf";
    const loadingToast = toast.loading(isPdf ? "Uploading PDF..." : "Uploading video...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/file", formData);
      const file_url = res.data.url;

      await createLesson({ 
        module_id: moduleId, 
        title: file.name, 
        type: lessonType, 
        content_url: file_url 
      });

      loadModules(selectedCourse);
      toast.dismiss(loadingToast);
      toast.success(isPdf ? "PDF uploaded!" : "Video uploaded!");
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Upload failed");
    }
  };

  const handleEditModule = (m) => {
    setEditingModule(m);
    setEditModuleTitle(m.title);
  };
  const handleUpdateModule = async () => {
    if (!editModuleTitle) return toast.error("Enter module title");
    try {
      await updateModule(editingModule.id, editModuleTitle);
      setEditingModule(null);
      loadModules(selectedCourse);
      toast.success("Module updated");
    } catch (err) {
      toast.error("Failed to update module");
    }
  };
  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm("Delete this module and all its lessons?")) return;
    try {
      await deleteModule(moduleId);
      loadModules(selectedCourse);
      toast.success("Module deleted");
    } catch (err) {
      toast.error("Failed to delete module");
    }
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setEditLessonTitle(lesson.title);
  };
  const handleUpdateLesson = async () => {
    if (!editLessonTitle) return toast.error("Enter lesson title");
    try {
      await updateLesson(editingLesson.id, editLessonTitle);
      setEditingLesson(null);
      loadModules(selectedCourse);
      toast.success("Lesson updated");
    } catch (err) {
      toast.error("Failed to update lesson");
    }
  };
  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      await deleteLesson(lessonId);
      loadModules(selectedCourse);
      toast.success("Lesson deleted");
    } catch (err) {
      toast.error("Failed to delete lesson");
    }
  };

  if (loading) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <div className="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={isEmbedded ? "w-full" : "min-h-screen bg-slate-50 dark:bg-slate-950 flex"}>
      {!isEmbedded && <Toaster position="top-center" />}
      {!isEmbedded && <Sidebar role="teacher" />}

      <main className={`flex-1 w-full space-y-10 max-w-7xl mx-auto ${isEmbedded ? "" : "lg:ml-64 p-6 lg:p-10"}`}>
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white">Manage Recorded Classes</h1>
            <p className="text-slate-500 dark:text-slate-400">Organize your course curriculum, upload recorded videos, and manage course galleries.</p>
          </div>
        </header>

        {!isEmbedded && (
        <section className="bg-white dark:bg-slate-900 p-6 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 shadow-sm">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-blue-600">
            <ClipboardCheck size={20} /> Select Course
          </h3>
          <select
            className="w-full max-w-md px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border dark:border-slate-800 rounded-xl font-bold shadow-sm outline-none focus:ring-2 focus:ring-blue-500/20"
            value={selectedCourse}
            onChange={(e) => {
              const val = Number(e.target.value);
              setSelectedCourse(val);
              loadModules(val);
              loadCourseImages(val);
            }}
          >
            {courses.length === 0 && <option value="">No courses available</option>}
            <option value="">-- Select Course --</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </section>
        )}

        {selectedCourse ? (
          <div className="space-y-10">
            {/* CREATE MODULE */}
            <div className="bg-white dark:bg-slate-900 p-6 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 shadow-sm space-y-4">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <FolderPlus size={18} className="text-blue-500" /> Create Module
              </h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  className="flex-1 px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium text-slate-900 dark:text-white"
                  placeholder="Module Title (e.g. Introduction to Physics)"
                  value={moduleTitle}
                  onChange={(e) => setModuleTitle(e.target.value)}
                />
                <button
                  onClick={handleCreateModule}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2"
                >
                  <Plus size={18} /> Add Module
                </button>
              </div>
            </div>

            {/* GALLERY MANAGEMENT */}
            <div className="space-y-6">
              <h2 className="text-xl font-bold dark:text-white flex items-center gap-2">
                <ImageIcon size={20} className="text-indigo-500" /> Course Gallery
              </h2>
              <div className="bg-white dark:bg-slate-900 p-6 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {courseImages.map((img) => (
                    <div key={img.id} className="relative aspect-video rounded-xl overflow-hidden border-2 border-slate-100 dark:border-slate-800">
                      <img
                        src={img.image_url}
                        alt="Course gallery item"
                        className="w-full h-full object-cover"
                      />
                      {img.is_preview === 1 && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded">PREVIEW</div>
                      )}
                    </div>
                  ))}
                  <label className="aspect-video rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors">
                    <Upload size={20} className="text-slate-400 mb-2" />
                    <span className="text-xs font-bold text-slate-500">Upload Screenshot</span>
                    <input type="file" className="hidden" accept="image/*" onChange={(e) => { handleImageUpload(e.target.files[0]); e.target.value = null; }} />
                  </label>
                </div>
              </div>
            </div>



            {/* CURRICULUM */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold dark:text-white flex items-center gap-2">
                  <FolderPlus size={20} className="text-amber-500" /> Curriculum
                </h2>
                <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg text-xs font-bold uppercase">
                  Active: {courses.find(c => c.id === selectedCourse)?.title}
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {modules.map((m) => (
                  <motion.div layout key={m.id} className="bg-white dark:bg-slate-900 p-5 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 shadow-sm group flex flex-col h-full">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-bold flex items-center gap-2 truncate text-slate-900 dark:text-white">
                        <FolderPlus className="text-indigo-500 shrink-0" size={18} /> {m.title}
                      </h4>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEditModule(m)} className="p-1.5 text-slate-400 hover:text-blue-600 bg-slate-50 dark:bg-slate-800 rounded"><Edit3 size={14} /></button>
                        <button onClick={() => handleDeleteModule(m.id)} className="p-1.5 text-slate-400 hover:text-rose-600 bg-slate-50 dark:bg-slate-800 rounded"><Trash2 size={14} /></button>
                      </div>
                    </div>

                    <div className="flex-1 space-y-2 mb-6 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                      {m.lessons && m.lessons.length > 0 ? (
                        m.lessons.map((lesson) => (
                          <div key={lesson.id} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-900/50 rounded-xl group/lesson hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors">
                            <div className="flex items-center gap-3 truncate">
                              <div className="p-1.5 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                                {lesson.type === "pdf" ? (
                                  <FileText size={14} className="text-rose-500" />
                                ) : (
                                  <PlayCircle size={14} className="text-blue-500" />
                                )}
                              </div>
                              <span className="text-xs text-slate-600 dark:text-slate-300 font-medium truncate">{lesson.title}</span>
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover/lesson:opacity-100 transition-opacity">
                              <button onClick={() => handleEditLesson(lesson)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded"><Edit3 size={12} /></button>
                              <button onClick={() => handleDeleteLesson(lesson.id)} className="p-1.5 text-slate-400 hover:text-rose-600 rounded"><Trash2 size={12} /></button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-8 text-center border-2 border-dashed border-slate-100 dark:border-slate-800 rounded-2xl">
                          <p className="text-[11px] text-slate-400 italic">No lessons yet</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-auto">
                      <input
                        id={`file-${m.id}`}
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => { handleUpload(e.target.files[0], m.id, "video"); e.target.value = null; }}
                      />
                      <input
                        id={`pdf-${m.id}`}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => { handleUpload(e.target.files[0], m.id, "pdf"); e.target.value = null; }}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => document.getElementById(`file-${m.id}`).click()}
                          className="py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-blue-600 hover:text-white text-slate-600 dark:text-slate-400 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
                        >
                          <Upload size={16} /> Video
                        </button>
                        <button
                          onClick={() => document.getElementById(`pdf-${m.id}`).click()}
                          className="py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-rose-600 hover:text-white text-slate-600 dark:text-slate-400 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
                        >
                          <FileText size={16} /> PDF
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-slate-400 font-bold">
            Select or create a course first to manage recorded classes.
          </div>
        )}
      </main>

      {/* EDIT MODULE MODAL */}
      <AnimatePresence>
        {editingModule && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2rem] shadow-2xl overflow-hidden border border-slate-100 dark:border-slate-800"
            >
              <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Edit Module</h3>
                <button onClick={() => setEditingModule(null)} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Module Title</label>
                <input
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium text-slate-900 dark:text-white"
                  value={editModuleTitle}
                  onChange={(e) => setEditModuleTitle(e.target.value)}
                />
              </div>
              <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 flex justify-end gap-3">
                <button onClick={() => setEditingModule(null)} className="px-5 py-2.5 font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button onClick={handleUpdateModule} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all">Save Changes</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* EDIT LESSON MODAL */}
      <AnimatePresence>
        {editingLesson && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2rem] shadow-2xl overflow-hidden border border-slate-100 dark:border-slate-800"
            >
              <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Edit Lesson</h3>
                <button onClick={() => setEditingLesson(null)} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Lesson Title</label>
                <input
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 font-medium text-slate-900 dark:text-white"
                  value={editLessonTitle}
                  onChange={(e) => setEditLessonTitle(e.target.value)}
                />
              </div>
              <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 flex justify-end gap-3">
                <button onClick={() => setEditingLesson(null)} className="px-5 py-2.5 font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button onClick={handleUpdateLesson} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all">Save Changes</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
